import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';

interface AppSessionValue {
  user: ReturnType<typeof useAuth>['user'];
  session: ReturnType<typeof useAuth>['session'];
  loading: boolean;
  isRecoveryMode: boolean;
  signOut: ReturnType<typeof useAuth>['signOut'];
  sessionStatus: ReturnType<typeof useAuth>['sessionStatus'];
  extendSession: ReturnType<typeof useAuth>['extendSession'];
  organization: ReturnType<typeof useOrganization>['organization'];
  role: ReturnType<typeof useOrganization>['role'];
  capabilities: ReturnType<typeof useOrganization>['capabilities'];
  hasCapability: ReturnType<typeof useOrganization>['hasCapability'];
  isCapabilitiesLoading: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isUser: boolean;
}

const AppSessionContext = createContext<AppSessionValue | null>(null);

export function AppSessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const organizationState = useOrganization();

  const value = useMemo<AppSessionValue>(
    () => ({
      user: auth.user,
      session: auth.session,
      loading: auth.loading,
      isRecoveryMode: auth.isRecoveryMode,
      signOut: auth.signOut,
      sessionStatus: auth.sessionStatus,
      extendSession: auth.extendSession,
      organization: organizationState.organization,
      role: organizationState.role,
      capabilities: organizationState.capabilities,
      hasCapability: organizationState.hasCapability,
      isCapabilitiesLoading: organizationState.isLoading,
      isAdmin: organizationState.isAdmin,
      isManager: organizationState.isManager,
      isUser: organizationState.isUser,
    }),
    [
      auth.user,
      auth.session,
      auth.loading,
      auth.isRecoveryMode,
      auth.signOut,
      auth.sessionStatus,
      auth.extendSession,
      organizationState.organization,
      organizationState.role,
      organizationState.capabilities,
      organizationState.hasCapability,
      organizationState.isLoading,
      organizationState.isAdmin,
      organizationState.isManager,
      organizationState.isUser,
    ],
  );

  return <AppSessionContext.Provider value={value}>{children}</AppSessionContext.Provider>;
}

export function useAppSession() {
  const context = useContext(AppSessionContext);
  if (!context) {
    throw new Error('useAppSession must be used within an AppSessionProvider');
  }
  return context;
}

export function useOptionalAppSession() {
  return useContext(AppSessionContext);
}
